import { useState } from 'react';
import { Linkedin, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SITE_CONFIG, CALENDLY_URL } from '@constants';
import { Modal, XIcon } from '@components/common';

export function Footer() {
  const currentYear = new Date().getFullYear();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as any).toString(),
    })
      .then(() => {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsModalOpen(false);
          setIsSubmitted(false);
        }, 2000);
      })
      .catch(error => {
        console.error('Form submission error:', error);
      });
  };

  const footerLinks = {
    services: [
      { label: 'All Services', to: '/services' },
      { label: 'Security Strategy', to: '/services/security-strategy' },
      { label: 'Risk Assessment', to: '/services/risk-assessment' },
      { label: 'Compliance', to: '/services/compliance' },
      { label: 'Incident Response', to: '/services/incident-response' },
      { label: 'Security Culture', to: '/services/security-culture' },
      { label: 'Board Reporting', to: '/services/board-reporting' },
      { label: 'Cyber Resilience', to: '/services/cyber-resilience' },
    ],
    packages: [
      { label: 'All Packages', to: '/packages' },
      { label: 'Security Maturity', to: '/packages/security-maturity-assessment' },
      { label: 'AI Governance', to: '/packages/ai-governance-setup' },
      { label: 'Technical Estate Review', to: '/packages/technical-estate-review' },
      { label: 'M&A Cyber Due Diligence', to: '/packages/ma-cyber-due-diligence' },
      { label: 'Board Tabletop Exercises', to: '/packages/board-tabletop-exercises' },
    ],
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'News', href: '/news' },
      { label: 'Knowledge Hub', href: '/knowledge-hub' },
      { label: 'Join Our CISO Network', href: '/join' },
      { label: 'How We Work', href: '/how-we-work' },
      { label: 'Careers', href: '/careers' },
    ],
    legal: [
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms of Service', href: '/terms-of-service' },
      { label: 'Locations', href: '/locations' },
      { label: 'Sectors', href: '/sectors' },
    ],
  };

  return (
    <>
      <footer className="bg-primary py-16 relative overflow-hidden">
        {/* Grid background with gradient fade */}
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: `linear-gradient(rgba(6, 182, 212, 0.5) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(6, 182, 212, 0.5) 1px, transparent 1px)`,
            backgroundSize: '20px 20px',
            maskImage: 'linear-gradient(to bottom, transparent, black 20%, black 80%, transparent)',
            WebkitMaskImage:
              'linear-gradient(to bottom, transparent, black 20%, black 80%, transparent)',
          }}
        />
        <div className="container-custom relative">
          <div className="grid md:grid-cols-5 gap-8 mb-12">
            {/* Brand */}
            <div className="md:col-span-1">
              <Link to="/" className="flex items-center gap-2 mb-2 group">
                <img
                  src="/images/logo.svg"
                  alt="The CISO Network"
                  width="96"
                  height="96"
                  className="h-24 w-auto transition-transform duration-300 group-hover:scale-105"
                />
              </Link>
              <p className="text-sm text-primary-foreground/50 leading-relaxed">
                <em>The</em> Fractional CISO Platform.
              </p>
              <p className="mt-4 text-sm text-primary-foreground/50 leading-relaxed">
                Providing world-class fractional CISO services to UK businesses that demand top-tier
                security leadership.
              </p>
              <div className="flex gap-4 mt-6">
                <a
                  href={SITE_CONFIG.social.linkedin}
                  aria-label="The CISO Network on LinkedIn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-foreground/50 hover:text-teal transition-colors"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
                <a
                  href={SITE_CONFIG.social.twitter}
                  aria-label="The CISO Network on X"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-foreground/50 hover:text-teal transition-colors"
                >
                  <XIcon className="h-5 w-5" />
                </a>
              </div>
            </div>

            {/* Services */}
            <div>
              <h3 className="font-heading font-semibold text-primary-foreground mb-4 text-sm uppercase tracking-wider">
                Services
              </h3>
              <ul className="space-y-2">
                {footerLinks.services.map(link => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-sm text-primary-foreground/50 hover:text-teal transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Packages */}
            <div>
              <h3 className="font-heading font-semibold text-primary-foreground mb-4 text-sm uppercase tracking-wider">
                Packages
              </h3>
              <ul className="space-y-2">
                {footerLinks.packages.map(link => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-sm text-primary-foreground/50 hover:text-teal transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="font-heading font-semibold text-primary-foreground mb-4 text-sm uppercase tracking-wider">
                Company
              </h3>
              <ul className="space-y-2">
                {footerLinks.company.map(link => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-sm text-primary-foreground/50 hover:text-teal transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-heading font-semibold text-primary-foreground mb-4 text-sm uppercase tracking-wider">
                Contact
              </h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-foreground/50 hover:text-teal transition-colors"
                  >
                    Book a call
                  </a>
                </li>
                <li>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="text-sm text-primary-foreground/50 hover:text-teal transition-colors flex items-center gap-1"
                  >
                    Message us
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-navy-light/30 pt-8 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
            <div className="text-xs text-primary-foreground/40 text-center sm:text-left">
              <p>
                ©{currentYear} {SITE_CONFIG.name}. All rights reserved.
              </p>
              <p className="mt-1">
                The CISO Network Ltd is a company registered in England and Wales. Company number
                17182533.
              </p>
            </div>
            <div className="flex gap-6">
              {footerLinks.legal.map(link => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="text-xs text-primary-foreground/40 hover:text-teal transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Send Us a Message">
        {isSubmitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-teal/20 flex items-center justify-center mb-4">
              <Send className="w-8 h-8 text-teal" />
            </div>
            <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
              Message Sent!
            </h3>
            <p className="text-muted-foreground">We'll get back to you soon.</p>
          </div>
        ) : (
          <form
            name="contact-form"
            method="POST"
            data-netlify="true"
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <input type="hidden" name="form-name" value="contact-form" />

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                placeholder="Your name"
                className="w-full px-4 py-3 rounded-lg bg-background border border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal transition-colors"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                placeholder="you@company.com"
                className="w-full px-4 py-3 rounded-lg bg-background border border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal transition-colors"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={4}
                placeholder="How can we help?"
                className="w-full px-4 py-3 rounded-lg bg-background border border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal transition-colors resize-none"
              />
            </div>

            <button type="submit" className="w-full btn-hero justify-center">
              Send Message <Send className="ml-2 h-4 w-4" />
            </button>
          </form>
        )}
      </Modal>
    </>
  );
}
